import React, { useEffect, useMemo } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';

import { ProjectLayoutModel } from '@paragon/contract-library';
import {
  getChildrenFinder,
  getComponentDesignDescendantGuids,
  getComponentDesignQuantities,
  getDescendants,
  getFlattenedChildrenWithCalculatedQuantity,
  getLayoutModelsWithPendingChanges,
} from '@paragon/layout-models';
import { InlineNotification } from '@paragon/notification-tools';
import { humanCase } from '@paragon/utilities';

import { ensureProjectMetrics } from '../store/actions/projectMetrics';
import { getTotalComponentCount } from '../store/selectors/projectMetrics';
import { useAppDispatch, useAppSelector } from './hooks';
import { ProjectDetailsFields } from './ProjectDetailsFields';

interface Props {
  readonly projectGuid: string;
  readonly focusProjectName?: boolean;
  readonly onProjectNameFocused?: () => void;
}

function Stat({ value, label }: { value: React.ReactNode; label: string }) {
  return (
    <Box
      sx={{
        minWidth: 56,
        px: 1,
        py: 0.5,
        textAlign: 'center',
        borderRadius: 1,
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="subtitle1">{value}</Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * The drawer's Project tab: the at-a-glance stat tiles above the grouped project detail fields.
 */
export function ProjectTab({ projectGuid, focusProjectName, onProjectNameFocused }: Props) {
  const dispatch = useAppDispatch();
  const project = useAppSelector(
    (state) => getLayoutModelsWithPendingChanges(state)[projectGuid] as ProjectLayoutModel | undefined,
  );
  const isSaved = useAppSelector((state) => state.layoutModels[projectGuid] != null);
  const getChildren = useAppSelector(getChildrenFinder);
  const totalComponentCount = useAppSelector(getTotalComponentCount);
  const flattenedChildrenGetter = useAppSelector(getFlattenedChildrenWithCalculatedQuantity);

  const quantityCounts = useMemo(
    () => Object.values(flattenedChildrenGetter(projectGuid)),
    [flattenedChildrenGetter, projectGuid],
  );

  const metricsEntry = useAppSelector((state) => state.projectMetrics[projectGuid]);
  const metrics = metricsEntry?.data;
  const metricsStatus = metricsEntry?.status;

  useEffect(() => {
    if (isSaved) void dispatch(ensureProjectMetrics(projectGuid));
  }, [dispatch, projectGuid, isSaved, metricsStatus]);

  const structure = useMemo(() => {
    const descendants = getDescendants(projectGuid, getChildren);
    const countDistinct = (type: string) =>
      new Set(descendants.filter((model) => model.type === type).map((model) => model.guid)).size;

    return {
      components: totalComponentCount(projectGuid),
      designs: getComponentDesignDescendantGuids(projectGuid, getChildren).length,
      buildings: countDistinct('building'),
      levels: countDistinct('level'),
    };
  }, [projectGuid, getChildren, totalComponentCount]);

  // Per-design board feet / plate counts multiplied by each design's quantity across the project.
  const materialTotals = useMemo(() => {
    if (!metrics) return null;

    const quantities = getComponentDesignQuantities(flattenedChildrenGetter(projectGuid));

    let boardFeet = 0;
    let plates = 0;

    for (const [componentDesignGuid, quantity] of Object.entries(quantities)) {
      if (quantity === 0) continue;

      const feet = metrics.feetByComponentDesignGuid[componentDesignGuid];
      const plateCount = metrics.numberOfPlates[componentDesignGuid];
      if (feet == null || plateCount == null) return null;

      boardFeet += feet.boardFeet * quantity;
      plates += plateCount * quantity;
    }

    return { boardFeet: Math.round(boardFeet), plates };
  }, [metrics, flattenedChildrenGetter, projectGuid]);

  const materialValue = (key: 'boardFeet' | 'plates') => {
    if (!isSaved || metricsStatus === 'error') return '—';
    if (metricsStatus !== 'loaded') return '…';
    if (materialTotals == null) return '—';
    return materialTotals[key].toLocaleString();
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, px: 1.5, pt: 2.5, pb: 0.5 }}>
        <Stat value={structure.components.toLocaleString()} label="Components" />
        <Stat value={structure.designs.toLocaleString()} label="Designs" />
        {structure.buildings > 0 && <Stat value={structure.buildings} label="Buildings" />}
        {structure.levels > 0 && <Stat value={structure.levels} label="Levels" />}
        <Stat value={materialValue('boardFeet')} label="Board Feet" />
        <Stat value={materialValue('plates')} label="Plates" />
      </Box>

      {metricsStatus === 'error' && (
        <Box role="alert" sx={{ px: 2 }}>
          <InlineNotification type="error">
            <Box component="span" sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, gap: 1 }}>
              <Box component="span" sx={{ flex: 1 }}>
                Couldn&apos;t load board feet and plate counts.
              </Box>
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                sx={{ flexShrink: 0 }}
                onClick={() => void dispatch(ensureProjectMetrics(projectGuid, true))}
              >
                Retry
              </Button>
            </Box>
          </InlineNotification>
        </Box>
      )}

      {project && (
        <ProjectDetailsFields
          project={project}
          focusProjectName={focusProjectName}
          onProjectNameFocused={onProjectNameFocused}
        />
      )}

      {quantityCounts.length > 0 && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Quantity Counts
          </Typography>
          <Grid container spacing={1}>
            {quantityCounts.map(({ model, quantity }, index) => (
              <Grid key={`${model.guid}-${index}`} size={6}>
                <Typography variant="body2">
                  {humanCase(model.type)} {model.name} ({quantity})
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );
}
